import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ClientLayout } from "../components/ClientLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, Briefcase, User, Tag, X, Gift, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import SiteFooter from "../components/SiteFooter";

const ClientPlans = () => {
  const { user, aiFilingEnabled } = useAuth();
  const [aiFilingPrice, setAiFilingPrice] = useState(499);
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [activeOffers, setActiveOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [financialYear, setFinancialYear] = useState("");
  const [taxRegime, setTaxRegime] = useState("not_sure");
  const [submitting, setSubmitting] = useState(false);

  // Offer state
  const [offerCode, setOfferCode] = useState("");
  const [validatingOffer, setValidatingOffer] = useState(false);
  const [appliedOffer, setAppliedOffer] = useState(null);
  const [offerError, setOfferError] = useState("");

  const currentYear = new Date().getFullYear();
  const financialYears = [
    `${currentYear - 1}-${currentYear}`,
    `${currentYear - 2}-${currentYear - 1}`,
    `${currentYear - 3}-${currentYear - 2}`
  ];

  const [pendingRequest, setPendingRequest] = useState(null); // existing pending_payment request
  const [retrying, setRetrying] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, offersRes, requestsRes, configRes] = await Promise.all([
        api.get("/plans"),
        api.get("/offers/active").catch(() => ({ data: [] })),
        api.get("/requests").catch(() => ({ data: [] })),
        api.get("/config/public").catch(() => ({ data: {} })),
      ]);
      setPlans(plansRes.data);
      setActiveOffers(offersRes.data);
      if (configRes.data?.features?.ai_filing_price) {
        setAiFilingPrice(configRes.data.features.ai_filing_price);
      }
      // Check for any pending_payment request
      const pending = requestsRes.data.find(r => r.status === "pending_payment");
      if (pending) setPendingRequest(pending);
    } catch (err) {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  const handleRetryPayment = async () => {
    if (!pendingRequest) return;
    setRetrying(true);
    try {
      const res = await api.post(`/requests/${pendingRequest.id}/retry-payment`);
      const { payment_url, payment_id, gateway, request } = res.data;

      if (gateway === "mock" || !payment_url) {
        toast.success("Payment retried successfully.");
        navigate(`/request/${request.id}`);
        return;
      }

      if (gateway === "razorpay" || payment_url.startsWith("razorpay://")) {
        const parts = payment_url.replace("razorpay://", "").split("|");
        const [rzKeyId, rzOrderId, rzPaymentId] = parts;
        localStorage.setItem("pending_payment_id", rzPaymentId);
        localStorage.setItem("pending_request_id", request.id);

        const options = {
          key: rzKeyId,
          order_id: rzOrderId,
          name: "MoolTax",
          description: `Tax Filing — ${pendingRequest.plan_name}`,
          prefill: { name: "", email: "", contact: "" },
          theme: { color: "#1a1a1a" },
          handler: function () { navigate("/payment/callback"); },
          modal: {
            ondismiss: function () {
              toast.error("Payment cancelled. Use 'Retry Payment' to try again.");
              setRetrying(false);
            }
          }
        };

        const openCheckout = () => { const rzp = new window.Razorpay(options); rzp.open(); };
        if (window.Razorpay) { openCheckout(); }
        else {
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.onload = openCheckout;
          document.body.appendChild(script);
        }
        return;
      }

      // PhonePe
      localStorage.setItem("pending_payment_id", payment_id);
      localStorage.setItem("pending_request_id", request.id);
      window.location.href = payment_url;

    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to retry payment");
    } finally {
      setRetrying(false);
    }
  };

  const handleCancelPendingRequest = async () => {
    if (!pendingRequest) return;
    setCancelling(true);
    try {
      await api.delete(`/requests/${pendingRequest.id}/cancel`);
      setPendingRequest(null);
      toast.success("Cancelled. You can now select a new plan.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setOfferError("");
    setFinancialYear("");
    setTaxRegime("not_sure");
    setShowDialog(true);

    // Auto-apply best applicable offer for this plan
    const applicable = activeOffers.filter(o =>
      !o.applicable_plans || o.applicable_plans.length === 0 || o.applicable_plans.includes(plan.id)
    );
    if (applicable.length > 0) {
      const best = applicable.reduce((a, b) => b.discount_value > a.discount_value ? b : a);
      setAppliedOffer(best);
      setOfferCode(best.code);
    } else {
      setAppliedOffer(null);
      setOfferCode("");
    }
  };

  const handleApplyOffer = async () => {
    if (!offerCode.trim()) return;
    
    setValidatingOffer(true);
    setOfferError("");
    
    try {
      const res = await api.post("/offers/validate", {
        code: offerCode,
        email: user?.email || "",
        phone: user?.phone || "",
        user_id: user?.id || ""
      });
      
      // Check if offer applies to this plan
      if (res.data.applicable_plans && !res.data.applicable_plans.includes(selectedPlan.id)) {
        setOfferError("This offer is not applicable to the selected plan");
        return;
      }
      
      setAppliedOffer(res.data);
      toast.success("Offer applied successfully!");
    } catch (err) {
      setOfferError(err.response?.data?.detail || "Invalid offer code");
    } finally {
      setValidatingOffer(false);
    }
  };

  const removeOffer = () => {
    setAppliedOffer(null);
    setOfferCode("");
    setOfferError("");
  };

  const calculateFinalPrice = () => {
    if (!selectedPlan) return 0;
    if (!appliedOffer) return selectedPlan.price;
    
    if (appliedOffer.discount_type === "percentage") {
      return selectedPlan.price - (selectedPlan.price * appliedOffer.discount_value / 100);
    }
    return Math.max(0, selectedPlan.price - appliedOffer.discount_value);
  };

  const handleSubmit = async () => {
    if (!financialYear) {
      toast.error("Please select a financial year");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        plan_id: selectedPlan.id,
        financial_year: financialYear,
        tax_regime: taxRegime
      };
      
      if (appliedOffer) {
        payload.offer_code = appliedOffer.code;
        payload.offer_email = user?.email;
        payload.offer_phone = user?.phone;
      }
      
      // Creates request + initiates payment in one call
      const res = await api.post("/requests", payload);

      const { payment_url, payment_id, gateway, request } = res.data;

      setShowDialog(false);

      // Mock gateway — skip redirect, go straight to request page
      if (gateway === "mock" || !payment_url) {
        toast.success("Request created! Proceeding with mock payment.");
        navigate(`/request/${request.id}`);
        return;
      }

      // Razorpay — popup flow (no full page redirect)
      if (gateway === "razorpay" || payment_url.startsWith("razorpay://")) {
        const parts = payment_url.replace("razorpay://", "").split("|");
        const [rzKeyId, rzOrderId, rzPaymentId] = parts;

        localStorage.setItem("pending_payment_id", rzPaymentId);
        localStorage.setItem("pending_request_id", request.id);

        const options = {
          key: rzKeyId,
          order_id: rzOrderId,
          name: "MoolTax",
          description: `Tax Filing — ${selectedPlan.name}`,
          prefill: {
            name: user?.name || "",
            email: user?.email || "",
            contact: user?.phone || "",
          },
          theme: { color: "#1a1a1a" },
          handler: function (response) {
            // Payment successful — go to callback page to confirm
            navigate("/payment/callback");
          },
          modal: {
            ondismiss: function () {
              toast.error("Payment cancelled. Use 'Retry Payment' below to try again, or cancel to choose a different plan.");
              // Refresh pending request state
              setPendingRequest({ id: request.id, plan_name: selectedPlan?.name, price: calculateFinalPrice() });
              setShowDialog(false);
            }
          }
        };

        const rzScript = document.getElementById("razorpay-script");
        const openCheckout = () => {
          const rzp = new window.Razorpay(options);
          rzp.open();
        };

        if (window.Razorpay) {
          openCheckout();
        } else if (!rzScript) {
          const script = document.createElement("script");
          script.id = "razorpay-script";
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.onload = openCheckout;
          document.body.appendChild(script);
        }
        return;
      }

      // PhonePe — full page redirect
      localStorage.setItem("pending_payment_id", payment_id);
      localStorage.setItem("pending_request_id", request.id);

      toast.success("Redirecting to payment...");
      window.location.href = payment_url;

    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "Failed to create request";

      if (status === 403) {
        toast.error(msg); // Email not verified
        return;
      }
      if (status === 402) {
        // pending_payment_exists — fetch the pending request and show banner
        toast.error("You have an incomplete payment. Please complete or cancel it first.");
        const requestsRes = await api.get("/requests").catch(() => ({ data: [] }));
        const pending = requestsRes.data.find(r => r.status === "pending_payment");
        if (pending) setPendingRequest(pending);
        setShowDialog(false);
        return;
      }
      if (status === 503) {
        toast.error(msg); // Intake paused
        return;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const salaryPlans = plans.filter(p => p.plan_type === 'salary');
  const businessPlans = plans.filter(p => p.plan_type === 'business');

  return (
    <ClientLayout>
      <div className="space-y-8">

        {/* Pending Payment Banner */}
        {pendingRequest && (
          <div
            className="rounded-xl border-2 p-5"
            style={{ borderColor: "var(--accent)", backgroundColor: "#fff8f0" }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-semibold" style={{ color: "var(--primary)" }}>
                  Incomplete payment for {pendingRequest.plan_name}
                </p>
                <p className="text-sm opacity-70 mt-0.5">
                  ₹{pendingRequest.price?.toLocaleString()} — payment was not completed.
                  Complete it to proceed, or cancel to choose a different plan.
                </p>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <button
                  onClick={handleCancelPendingRequest}
                  disabled={cancelling || retrying}
                  className="text-sm px-4 py-2 rounded-lg border disabled:opacity-50"
                  style={{ borderColor: "var(--border)" }}
                >
                  {cancelling ? "Cancelling..." : "Cancel & choose again"}
                </button>
                <button
                  onClick={handleRetryPayment}
                  disabled={retrying || cancelling}
                  className="btn-primary text-sm px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {retrying ? "Opening payment..." : "Retry Payment"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>Choose Your ITR Filing Plan</h1>
          <p className="opacity-70 mt-1">
            Select the right plan for income tax return filing based on your salary, business income, or complexity.
          </p>
        </div>

        {/* Active Offers Banner */}
        {activeOffers.length > 0 && (
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Gift size={20} className="text-orange-500" />
              <span className="font-semibold text-orange-800">Special Offers Available!</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {activeOffers.map((offer, idx) => (
                <Badge 
                  key={idx} 
                  className="bg-orange-100 text-orange-700 hover:bg-orange-200 cursor-pointer"
                  onClick={() => setOfferCode(offer.code)}
                >
                  {offer.code}: {offer.discount_type === "percentage" ? `${offer.discount_value}% OFF` : `₹${offer.discount_value} OFF`}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : plans.length === 0 ? (
          <div className="empty-state bg-white rounded-xl border" style={{ borderColor: 'var(--border)' }}>
            <p>No plans available at the moment. Please check back later.</p>
          </div>
        ) : (
          <>
            {/* Salary Plans */}
            {salaryPlans.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-6 h-6" style={{ color: 'var(--accent)' }} />
                  <h2 className="text-xl font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>For Salaried Individuals</h2>
                </div>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {salaryPlans.map((plan) => (
                    <PlanCard key={plan.id} plan={plan} onSelect={handleSelectPlan} activeOffers={activeOffers} />
                  ))}
                </div>
              </div>
            )}

            {/* AI Filing Plan */}
            {aiFilingEnabled && (
              <div className="mt-12">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-6 h-6" style={{ color: 'var(--accent)' }} />
                  <h2 className="text-xl font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>
                    AI Tax Filing — File Yourself
                  </h2>
                </div>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="rounded-2xl overflow-hidden"
                       style={{ border: '2px solid var(--accent)', boxShadow: '0 4px 20px rgba(194,94,0,0.15)' }}>
                    <div className="text-center text-xs font-bold py-2 text-white"
                         style={{ backgroundColor: 'var(--accent)' }}>
                      ✨ AI POWERED — INSTANT ITR JSON
                    </div>
                    <div className="bg-white p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-semibold px-3 py-1 rounded-full"
                              style={{ backgroundColor: '#fff3e0', color: 'var(--accent)' }}>
                          AI Filing
                        </span>
                      </div>
                      <h3 className="text-lg font-bold mb-1"
                          style={{ fontFamily: 'Fraunces, serif', color: 'var(--primary)' }}>
                        AI Tax Filing
                      </h3>
                      <p className="text-sm mb-4" style={{ opacity: 0.6, lineHeight: 1.5 }}>
                        Upload your documents and AI generates your complete ITR JSON in minutes.
                        File directly on incometax.gov.in yourself.
                      </p>
                      <div className="flex items-end gap-2 mb-4">
                        <span className="text-3xl font-bold"
                              style={{ color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>
                          ₹{aiFilingPrice.toLocaleString()}
                        </span>
                        <span className="text-sm mb-1 opacity-50">per filing</span>
                      </div>
                      <ul className="space-y-2 mb-6">
                        {[
                          'Covers ITR-1, ITR-2, ITR-3, ITR-4',
                          'Old vs New regime — AI picks lower tax',
                          'All deductions: 80C, 80D, HRA, Capital Gains',
                          'Multiple employers supported',
                          'Download ITR JSON, file yourself',
                        ].map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                            <span style={{ opacity: 0.8 }}>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => navigate('/ai-filing')}
                        className="w-full btn-primary py-3 rounded-lg font-semibold">
                        Start AI Filing
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Business Plans */}
            {businessPlans.length > 0 && (
              <div className="mt-12">
                <div className="flex items-center gap-2 mb-4">
                  <Briefcase className="w-6 h-6" style={{ color: 'var(--accent)' }} />
                  <h2 className="text-xl font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>For Business Owners</h2>
                </div>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {businessPlans.map((plan) => (
                    <PlanCard key={plan.id} plan={plan} onSelect={handleSelectPlan} activeOffers={activeOffers} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Select Financial Year Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fraunces, serif' }}>Start Tax Filing</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
              <p className="font-semibold">{selectedPlan?.name}</p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm opacity-70">Plan Price</span>
                <div className="text-right">
                  {appliedOffer ? (
                    <div className="flex items-end gap-2 justify-end">
                      <span className="text-base line-through opacity-40">₹{selectedPlan?.price.toLocaleString()}</span>
                      <span className="text-xl font-bold mono" style={{ color: 'var(--accent)' }}>
                        ₹{calculateFinalPrice().toLocaleString()}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xl font-bold mono" style={{ color: 'var(--accent)' }}>
                      ₹{selectedPlan?.price.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
              {appliedOffer && (
                <p className="text-xs mt-1 text-right" style={{ color: '#2e7d32' }}>
                  {appliedOffer.code} — {appliedOffer.discount_type === "percentage"
                    ? `${appliedOffer.discount_value}% off`
                    : `₹${appliedOffer.discount_value} off`} applied
                </p>
              )}
            </div>
            
            <div>
              <Label htmlFor="fy">Select Financial Year</Label>
              <Select value={financialYear} onValueChange={setFinancialYear}>
                <SelectTrigger className="mt-2" data-testid="fy-select">
                  <SelectValue placeholder="Choose financial year" />
                </SelectTrigger>
                <SelectContent>
                  {financialYears.map((fy) => (
                    <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="tax-regime">Choose Tax Regime</Label>
              <Select value={taxRegime} onValueChange={setTaxRegime}>
                <SelectTrigger className="mt-2" data-testid="tax-regime-select">
                  <SelectValue placeholder="Choose tax regime" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="old">Old Regime</SelectItem>
                  <SelectItem value="new">New Regime</SelectItem>
                  <SelectItem value="not_sure">Not Sure</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs opacity-60 mt-2">
                This helps us suggest only relevant documents and avoid unnecessary uploads.
              </p>
            </div>
            {/* Offer Code Section */}
            <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
              <Label className="flex items-center gap-2">
                <Tag size={16} />
                {appliedOffer ? "Offer applied" : "Have an offer code?"}
              </Label>

              {appliedOffer ? (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-green-800">
                      {appliedOffer.code} — auto-applied!
                    </p>
                    <p className="text-sm text-green-600">
                      {appliedOffer.discount_type === "percentage"
                        ? `${appliedOffer.discount_value}% discount`
                        : `₹${appliedOffer.discount_value} off`}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={removeOffer} className="text-red-500"
                    title="Remove offer">
                    <X size={18} />
                  </Button>
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <Input
                    value={offerCode}
                    onChange={(e) => {
                      setOfferCode(e.target.value.toUpperCase());
                      setOfferError("");
                    }}
                    placeholder="Enter offer code"
                    className="flex-1 uppercase"
                    data-testid="offer-code-input"
                  />
                  <Button
                    variant="outline"
                    onClick={handleApplyOffer}
                    disabled={!offerCode.trim() || validatingOffer}
                  >
                    {validatingOffer ? "..." : "Apply"}
                  </Button>
                </div>
              )}
              {offerError && (
                <p className="text-sm text-red-500 mt-1">{offerError}</p>
              )}
            </div>

            {/* Final Price */}
            {appliedOffer && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm line-through opacity-50">₹{selectedPlan?.price.toLocaleString()}</span>
                  <div className="text-right">
                    <p className="text-xs text-green-600 mb-1">After discount</p>
                    <span className="text-2xl font-bold mono text-green-700">
                      ₹{calculateFinalPrice().toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col gap-2">
            <p className="text-xs opacity-60 text-center w-full">
              All prices are inclusive of GST. No additional charges at checkout.
            </p>
            <div className="flex gap-2 w-full justify-end">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting || !financialYear} className="btn-primary" data-testid="confirm-plan-btn">
                {submitting ? <span className="spinner" style={{ width: 20, height: 20 }}></span> : `Pay ₹${calculateFinalPrice().toLocaleString()} & Continue`}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="mt-12">
        <SiteFooter />
      </div>
    </ClientLayout>
  );
};

const PlanCard = ({ plan, onSelect, activeOffers = [] }) => {
  // Find best applicable offer for this plan
  const bestOffer = activeOffers.filter(
    (o) => !o.applicable_plans || o.applicable_plans.length === 0 || o.applicable_plans.includes(plan.id)
  ).reduce((best, o) => {
    if (!best) return o;
    return o.discount_value > best.discount_value ? o : best;
  }, null);

  const discountedPrice = bestOffer
    ? bestOffer.discount_type === "percentage"
      ? Math.max(0, plan.price - (plan.price * bestOffer.discount_value / 100))
      : Math.max(0, plan.price - bestOffer.discount_value)
    : plan.price;

  const hasDiscount = bestOffer && discountedPrice < plan.price;

  return (
    <div className="plan-card card-hover h-full flex flex-col" data-testid={`plan-card-${plan.id}`}>
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>{plan.name}</h3>
        {hasDiscount && (
          <span
            className="text-xs font-semibold px-2 py-1 rounded-full ml-2 flex-shrink-0"
            style={{ backgroundColor: '#fce4ec', color: '#c62828' }}
          >
            {bestOffer.discount_type === "percentage"
              ? `${bestOffer.discount_value}% OFF`
              : `₹${bestOffer.discount_value} OFF`}
          </span>
        )}
      </div>
      <p className="opacity-70 text-sm mb-4 flex-grow-0">{plan.description}</p>

      <div className="mb-1 flex items-end gap-2">
        <span className="text-3xl font-bold mono" style={{ color: 'var(--accent)' }}>
          ₹{discountedPrice.toLocaleString()}
        </span>
        {hasDiscount && (
          <span className="text-base mb-1 line-through opacity-40">
            ₹{plan.price.toLocaleString()}
          </span>
        )}
      </div>
      {hasDiscount && (
        <p className="text-xs mb-3" style={{ color: '#2e7d32' }}>
          Code <strong>{bestOffer.code}</strong> applied at checkout
        </p>
      )}
      {!hasDiscount && <div className="mb-4" />}

      <div className="mb-6 flex-grow">
        <p className="text-sm font-semibold mb-2">What's Included:</p>
        <ul className="space-y-2">
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--success)' }} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-6">
        <p className="text-sm font-semibold mb-2">Required Documents:</p>
        <ul className="space-y-1">
          {plan.required_documents.map((doc, idx) => (
            <li key={idx} className="text-sm opacity-70">• {doc}</li>
          ))}
        </ul>
      </div>

      <Button 
        onClick={() => onSelect(plan)} 
        className="w-full btn-primary mt-auto"
        data-testid={`select-plan-${plan.id}`}
      >
        Select Plan
      </Button>
    </div>
  );
};

export default ClientPlans;